/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { DomainToken } from 'client-code/domain-tokens';

import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { DeviceFeatures, DeviceModel } from '../../device';
import {
    DomainTokensService,
    Services,
    SlsServiceGlobalEndpointsService,
} from '../../infrastructure';
import { Log } from '../../logging';
import { Storage } from '../../storage/Storage';
import { ITivoSubscription, TivoSubscriptionModel } from '../../tivo';
import { SDTState } from '../enums';
import { SDTInfo } from '../interfaces/index';
import { TScopedDomainTokenNotifier } from '../types';

export class ScopedDomainTokenModel {
    private static TAG: string = 'ScopedDomainTokenModel';

    private static oScopedDomainTokenModel: ScopedDomainTokenModel | null = null;

    private oITivoSubscription: ITivoSubscription<TScopedDomainTokenNotifier>;

    private constructor() {
        this.oITivoSubscription = new TivoSubscriptionModel<TScopedDomainTokenNotifier>();
    }

    // eslint-disable-next-line class-methods-use-this
    private _commonParameters() {
        return {
            accessToken: DeviceModel.getAccessToken(),
            bodyId: DeviceModel.getTiVoSettings().getBodyId(),
            whiny: DeviceFeatures.isWhinyLoggingEnabled().toString(),
        };
    }

    private _commonParametersWithTimeZone() {
        return {
            ...this._commonParameters(),
            timeZone: DeviceModel.getTVProperties().getDefaultTimeZone(),
        };
    }

    // eslint-disable-next-line class-methods-use-this
    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](ScopedDomainTokenModel.TAG, message, ...optionalParams);
        Log[level[0]](ScopedDomainTokenModel.TAG, message, ...optionalParams);
        if (level === 'error')
            Diagnostics.error(ScopedDomainTokenModel.TAG, 'stacktrace:,', optionalParams[0]);
    }

    // returns the new calculated expiration time which is 90% to 95% of original time
    // eslint-disable-next-line class-methods-use-this
    private sdtRefreshTime(domainExpirationTime: number): number {
        // generates number between 5 to 10 (inclusive)
        const expirationRandomFactor = Math.floor(Math.random() * (10 - 5 + 1)) + 5;

        const expirationTimeInSeconds = domainExpirationTime * MILLISECONDS_IN_A_SECOND;

        /* Calculating number of hours more than current time and then.
         * Calculate expirationRandomFactor (range 5 to 10) percentage of expiration time.
         */
        const newDomainExpirationTime =
            (expirationTimeInSeconds - Date.now()) * (expirationRandomFactor / 100);
        return expirationTimeInSeconds - newDomainExpirationTime;
    }

    public static getInstance(): ScopedDomainTokenModel {
        if (!this.oScopedDomainTokenModel) {
            this.oScopedDomainTokenModel = new ScopedDomainTokenModel();
        }

        return this.oScopedDomainTokenModel;
    }

    // eslint-disable-next-line class-methods-use-this
    public getTokenFromStore(
        drmCustomDomain: string,
        drmCustomHeaderAuthScheme: string,
    ): DomainToken | null {
        const domainTokenKey = `${drmCustomDomain}#${drmCustomHeaderAuthScheme}`;
        let isTokenAvailableInStore: boolean = false;
        const scopedDomainTokenData = Storage.getInstance().getItem(
            Storage.Keys.SCOPE_DOMAIN_TOKENS,
        );

        if (scopedDomainTokenData !== null) {
            isTokenAvailableInStore = Object.keys(scopedDomainTokenData).includes(domainTokenKey);
        }

        return isTokenAvailableInStore ? scopedDomainTokenData[domainTokenKey] : null;
    }

    public async getToken(
        drmCustomDomain: string,
        drmCustomHeaderAuthScheme: string,
        isForced?: boolean,
        isRefreshed: boolean = false,
    ): Promise<DomainToken> {
        const scopedDomainTokenNotifyObject: TScopedDomainTokenNotifier = {
            sdtState: SDTState.SUCCESS,
            drmCustomDomain,
            drmCustomHeaderAuthScheme,
        };
        const domainTokenfromStorage = this.getTokenFromStore(
            drmCustomDomain,
            drmCustomHeaderAuthScheme,
        );
        const currentEpochTime = Date.now();
        const isScopeDomainTokenDataValid = !!(
            domainTokenfromStorage &&
            this.sdtRefreshTime(domainTokenfromStorage?.expiration) > currentEpochTime
        );

        let response;
        if (isForced || !isScopeDomainTokenDataValid) {

            response = await Services.getInstanceOf(DomainTokensService, {
                ...this._commonParametersWithTimeZone(),
                basePath: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                    SlsServiceGlobalEndpointsService.DOMAIN_TOKENS,
                ),
            }).fetchScopedDomainTokens(drmCustomDomain, drmCustomHeaderAuthScheme);

            if (response.hasError) {
                this.log(
                    'error',
                    `Failed to fetch scope domain tokens for: ${drmCustomDomain}`,
                    response.errorCode,
                );
                scopedDomainTokenNotifyObject.sdtState = SDTState.ERROR;
                this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
                throw new Error(response.errorCode || 'Failed to fetch domain tokens');
            }
        }

        const domainToken =
            isForced || !isScopeDomainTokenDataValid
                ? response.domainToken
                : domainTokenfromStorage;

        if (
            !domainToken?.token ||
            domainToken?.token === '' ||
            !domainToken?.expiration ||
            domainToken?.expiration === ''
        ) {
            this.log('error', `scope domain data missing for: ${drmCustomDomain}`);
            scopedDomainTokenNotifyObject.sdtState = SDTState.ERROR;
            this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
            throw new Error(response.errorCode || 'Scope Domian Token missing');
        }

        if (isRefreshed) {
            scopedDomainTokenNotifyObject.sdtState = SDTState.REFRESH;
            this.log('info', `${drmCustomDomain} scope domain token refreshed`);
            this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
        } else {
            this.log('info', `${drmCustomDomain} scope domain token fetched successfully`);
            this.oITivoSubscription.notify(scopedDomainTokenNotifyObject);
        }

        return domainToken;
    }

    public getSDTFetchListener(): ITivoSubscription<TScopedDomainTokenNotifier> {
        return this.oITivoSubscription;
    }

    public getTokenToBeRefreshed(): SDTInfo | null {
        const scopedDomainTokenData = Storage.getInstance().getItem(
            Storage.Keys.SCOPE_DOMAIN_TOKENS,
        );

        if (!scopedDomainTokenData) return null;

        let lowestExpirationTime: number = -1;
        let domainTokenKey: string = '';
        Object.keys(scopedDomainTokenData).forEach((domain) => {
            if (lowestExpirationTime === -1) {
                lowestExpirationTime = scopedDomainTokenData[domain].expiration;
                domainTokenKey = domain;
            } else if (scopedDomainTokenData[domain].expiration < lowestExpirationTime) {
                lowestExpirationTime = scopedDomainTokenData[domain].expiration;
                domainTokenKey = domain;
            }
        });
        let refreshTime: number = this.sdtRefreshTime(lowestExpirationTime);

        // Calculate delay from current time to refresh time
        const currentTime = Date.now();
        refreshTime = Math.max(refreshTime - currentTime, 0);
        /*
         ** This will extract drmCustomDomain and drmCustomHeaderAuthScheme
         ** from domainTokenKey (drmCustomDomain#drmCustomHeaderAuthScheme)
         */
        const domainAndAuthScheme = domainTokenKey.split('#');
        return {
            drmCustomDomain: domainAndAuthScheme[0],
            drmCustomHeaderAuthScheme: domainAndAuthScheme[1],
            refreshTime,
        } as SDTInfo;
    }
}
